/**
 * Centralized user-visible strings for the meeting demo.
 *
 * Rules (enforced by `scripts/verify.mjs` and `scripts/scan-strings.mjs`):
 *  - every zh-CN key exists in en-US with the same `{param}` placeholders;
 *  - no component renders a user-visible literal — components call `t('key')`.
 *
 * Placeholders are `{name}` and are replaced literally (no HTML, no ICU plurals); Chinese has no
 * plural forms and the English copy is written to read correctly for 1 and n.
 */

export const enUS = {
  /* ------------------------------------------------------------------ shell */
  'app.brand': '{brand} Meeting',
  'app.tagline': 'online · live RTC',
  'app.offlineTag': 'offline demo',
  'lang.label': 'Language',
  'lang.zh': '中文',
  'lang.en': 'English',
  'theme.toLight': 'Switch to light theme',
  'theme.toDark': 'Switch to dark theme',
  'theme.light': 'Light',
  'theme.dark': 'Dark',
  'common.close': 'Close',
  'common.cancel': 'Cancel',
  'common.copy': 'Copy',
  'common.copied': 'Copied',
  'common.send': 'Send',
  'common.you': 'you',
  'common.system': 'system',
  'common.unknown': 'unknown',
  'common.loading': 'Loading…',
  'common.dash': '-',

  /* ------------------------------------------------------------------ scenes */
  'scene.label': 'Scenario',
  'scene.hint': 'Pick the demo scenario. The URL parameter ?scene= keeps it shareable.',
  'scene.meeting': 'Meeting',
  'scene.meeting.title': 'Conference meeting',
  'scene.meeting.desc':
    'Gallery, moderation, chat and whiteboard — the existing AgoraMeeting parity demo.',
  'scene.oneToOne': '1v1 call',
  'scene.oneToOne.title': 'One-to-one call',
  'scene.oneToOne.desc':
    'Two parties, full-screen remote video, call controls, invite and token renewal.',
  'scene.live': 'Interactive live',
  'scene.live.title': 'Interactive live',
  'scene.live.desc':
    'Host publishes, audience watches and comments; the token denies audience publishing.',
  'scene.voice': 'Voice room',
  'scene.voice.title': 'Voice chat room',
  'scene.voice.desc':
    'Audio-only room with 8 mic seats, host approval of raised hands and text chat.',

  /* ------------------------------------------------------------------ join */
  'join.title': 'Join a meeting',
  'join.subtitle': 'A UIKit-style RTC experience on the ni-meet SDK',
  'join.lede':
    'Enter the meeting number your host shared. Joining publishes your real camera and microphone to the live SFU; there is no simulated media mode.',
  'join.ledeFake':
    'Offline demo mode is on: media is simulated in this tab and no control plane is contacted. Turn it off to join the live SFU with a real token.',
  'join.field.channel': 'Meeting number',
  'join.field.name': 'Your name',
  'join.field.role': 'Join as',
  'join.field.camera': 'Camera',
  'join.field.microphone': 'Microphone',
  'join.field.scene': 'Scenario',
  'join.placeholder.channel': 'weekly-sync',
  'join.placeholder.name': 'Ada Lovelace',
  'join.mic.on': 'Mic on',
  'join.mic.off': 'Mic off',
  'join.camera.on': 'Camera on',
  'join.camera.off': 'Camera off',
  'join.video.on': 'Video on',
  'join.video.off': 'Video off',
  'join.role.host': 'Host',
  'join.role.audience': 'Audience',
  'join.role.listener': 'Mic listener',
  'join.instant': 'Start a meeting now',
  'join.instantHint': 'Creates a meeting number and drops you in as host — share the link to invite others.',
  'join.guestName': 'Guest',
  'join.submit': 'Join meeting',
  'join.submitFake': 'Join demo meeting',
  'join.demo.toggle': 'Offline demo mode',
  'join.demo.hint':
    'Simulates the media plane inside this tab (no LiveKit edge, no token endpoint). Useful on a host without the Docker stack.',
  'join.device.defaultCamera': 'Default camera',
  'join.device.defaultMicrophone': 'Default microphone',
  'join.device.cameraN': 'Camera {index}',
  'join.device.microphoneN': 'Microphone {index}',
  'join.preview.off': 'Camera is off',
  'join.preview.unsupported':
    'Camera preview needs a browser with media devices (open this page on localhost or HTTPS).',
  'join.preview.unavailable':
    'Camera unavailable ({reason}). Pick another camera below or join with Camera off.',
  'join.insecure':
    'Camera and microphone are blocked on this origin. Open http://localhost:8083 (or this machine’s 127.0.0.1) instead of a LAN IP; for other devices use HTTPS.',
  'join.note.online':
    'This page runs online: tokens come from the control plane and media flows through LiveKit. Camera/microphone require a secure context (localhost, 127.0.0.1, or HTTPS).',
  'join.note.fake':
    'Offline demo mode: tokens and media are simulated in-process. Nothing is sent to the API or the SFU.',
  'join.error.tokenEndpoint':
    'RTC token endpoint is not configured; this demo only runs against the live control plane. Use ?fake=1 for the offline demo.',
  'join.error.token': 'Cannot obtain an RTC token from {endpoint}: {reason}',
  'join.error.channelRequired': 'Enter the meeting number',
  'join.error.nameRequired': 'Enter the name other participants will see',
  'join.error.roleDenied': '{scene} allows the host role only. Join as host to publish media.',
  'join.error.offlineDenied':
    'Offline demo mode does not need a control plane; uncheck it to join live.',

  /* ------------------------------------------------------------------ shared room chrome */
  'room.sceneBadge': '{scene}',
  'room.reconnecting': 'Reconnecting… media continues locally.',
  'room.locked': 'Meeting locked: new participants are asked to wait.',
  'room.recording.start': 'Start cloud recording',
  'room.recording.stop': 'Stop recording',
  'room.share.self': 'You are sharing your screen',
  'room.leave': 'Leave',
  'room.leaveTitle': 'Leave meeting',
  'room.stay': 'Stay',
  'room.leaveBody': 'Leave {channel}?',
  'room.leaveBodyHost':
    'Leave {channel}? Hosts leaving ends the meeting for everyone in this browser.',
  'room.controls': 'Meeting controls',
  'room.renew': 'Renew token',
  'room.renewed': 'Token renewed',
  'room.invite': 'Invite',
  'room.settings': 'Settings',
  'room.participants': 'Participants',
  'room.sceneLabel': 'Scenario',

  /* ------------------------------------------------------------------ meeting room */
  'meeting.control.micOn': 'Mute microphone',
  'meeting.control.micOff': 'Unmute microphone',
  'meeting.control.cameraOn': 'Turn camera off',
  'meeting.control.cameraOff': 'Turn camera on',
  'meeting.control.share': 'Share screen',
  'meeting.control.chat': 'Chat',
  'meeting.control.board': 'Whiteboard',
  'meeting.control.people': 'Participants',
  'meeting.control.hand': 'Raise hand',
  'meeting.control.manage': 'Meeting management',
  'meeting.control.settings': 'Settings',
  'meeting.badge.micOff': 'mic off',
  'meeting.badge.host': 'host',
  'meeting.badge.quality.unknown': 'unknown',
  'meeting.badge.quality.excellent': 'excellent',
  'meeting.badge.quality.good': 'good',
  'meeting.badge.quality.fair': 'fair',
  'meeting.badge.quality.poor': 'poor',
  'meeting.badge.quality.bad': 'bad',
  'meeting.badge.quality.down': 'down',
  'meeting.participants.title': 'Participants ({count})',
  'meeting.participants.muteAll': 'Mute all',
  'meeting.participants.lock': 'Lock meeting',
  'meeting.participants.locked': 'Locked',
  'meeting.participants.muted': 'muted',
  'meeting.participants.handUp': 'hand up',
  'meeting.participants.remove': 'Remove',
  'meeting.participants.makeHost': 'Make host',
  'meeting.participants.makeAudience': 'Make audience',
  'meeting.chat.title': 'Chat',
  'meeting.chat.close': 'Close chat',
  'meeting.chat.placeholder': 'Message everyone',
  'meeting.chat.label': 'Chat message',
  'meeting.chat.historyRestored': '{count} earlier message(s) restored from this browser',
  'meeting.chat.clearHistory': 'Clear history',
  'system.historyCleared': 'Local chat history cleared',
  'meeting.board.title': 'Whiteboard',
  'meeting.board.close': 'Close whiteboard',
  'meeting.board.readOnly': 'Only hosts can draw. You are watching the board.',
  'meeting.board.label': 'Shared whiteboard',
  'meeting.board.eraser': 'Eraser',
  'meeting.board.width': 'Width',
  'meeting.board.undo': 'Undo',
  'meeting.board.clear': 'Clear',
  'meeting.board.swatch': 'Color {color}',
  'meeting.invite.title': 'Invite people',
  'meeting.invite.link': 'Meeting link',
  'meeting.invite.number': 'Meeting number',
  'meeting.invite.members': 'Members',
  'meeting.invite.membersCount': '{count} in this meeting',
  'meeting.invite.copy': 'Copy link',
  'meeting.invite.note':
    'Share this link in IM. The IM provider resolves the RTC token and uid automatically.',
  'meeting.settings.title': 'Settings',
  'meeting.settings.devices': 'Microphone / camera / speaker',
  'meeting.settings.defaultMic': 'System default microphone',
  'meeting.settings.defaultCamera': 'System default camera',
  'meeting.settings.allow': 'Allow & refresh',
  'meeting.settings.enumerating': 'Enumerating devices…',
  'meeting.settings.deviceCounts': '{microphones} mic · {cameras} camera · {speakers} speaker',
  'meeting.settings.quality': 'Video quality profile',
  'meeting.settings.qualityOption': '{resolution} / {fps}fps',
  'meeting.settings.modeLive': 'online · live RTC',
  'meeting.settings.qualityNext': 'Quality applies on the next publish',
  'meeting.settings.vbg': 'Virtual background',
  'meeting.settings.vbg.upload': 'Upload image',
  'meeting.settings.vbg.none': 'None',
  'meeting.settings.vbg.blur': 'Blur',
  'meeting.settings.vbg.image': 'Image',
  'meeting.settings.vbg.uploadHint': 'Upload an image below to use the “Image” background',
  'meeting.settings.vbg.note':
    'Compositing is applied to the local preview; the published track keeps the raw camera.',
  'meeting.settings.session': 'Session',
  'meeting.settings.mode': 'Mode',
  'meeting.settings.localUid': 'Local uid',
  'meeting.settings.rtt': 'RTT',
  'meeting.settings.bitrate': 'Bitrate',
  'meeting.manage.title': 'Meeting management',
  'meeting.manage.muteAll': 'Mute all',
  'meeting.manage.lock': 'Lock meeting',
  'meeting.manage.unlock': 'Unlock meeting',
  'meeting.manage.remove': 'Remove',
  'meeting.manage.hostOnly': 'Moderation is host-only: {allowed}',
  'meeting.manage.true': 'yes',
  'meeting.manage.false': 'no',
  'toast.inviteCopied': 'Invite link copied',
  'toast.mutedCount': 'Muted {count} participant(s)',
  'toast.participantRemoved': 'Participant removed',
  'toast.historyCleared': 'Local chat history cleared',
  'toast.meetingLocked': 'Meeting locked',
  'toast.meetingUnlocked': 'Meeting unlocked',
  'toast.permissionDenied': 'Camera/microphone permission was denied',
  'toast.reconnecting': 'Reconnecting…',
  'toast.offlineSeeded': 'Offline demo: simulated participants joined',
  'toast.tokenExpiring': 'The token expires soon; renewing automatically',
  'toast.tokenRenewed': 'Token renewed',
  'toast.tokenExpired': 'The meeting token expired',
  'toast.kicked': 'The host removed you from the meeting',
  'toast.mutedByHost': 'The host muted your microphone',
  'toast.approvedMic': 'The host approved your mic seat',
  'toast.voiceSeatGranted': '{name} is now on mic',
  'toast.seatsFull': 'All 8 mic seats are taken',
  'toast.notHost': 'Only the host can do that',

  /* ------------------------------------------------------------------ system messages */
  'system.joining': 'Joining {channel}',
  'system.joined': 'You joined {channel}',
  'system.left': '{name} left the meeting',
  'system.userJoined': '{name} joined the meeting',
  'system.hostMuted': 'Host muted {count} participant(s)',
  'system.removed': '{name} was removed by the host',
  'system.roleChanged': 'Role changed: {name} is now {role}',
  'system.role.host': 'host',
  'system.role.audience': 'audience',
  'system.role.listener': 'mic listener',
  'system.guest': 'User {uid}',

  /* ------------------------------------------------------------------ 1v1 */
  '1v1.title': 'One-to-one call',
  '1v1.waiting': 'Waiting for the other person to join…',
  '1v1.connected': 'Connected with {name}',
  '1v1.remoteOff': '{name}’s camera is off',
  '1v1.you': 'You',
  '1v1.invite': 'Invite',
  '1v1.devices': 'Devices',
  '1v1.renew': 'Renew token',
  '1v1.renewed': 'Token renewed',
  '1v1.mute': 'Mute',
  '1v1.unmute': 'Unmute',
  '1v1.cameraOn': 'Camera on',
  '1v1.cameraOff': 'Camera off',
  '1v1.hangUp': 'End call',
  '1v1.hangUpTitle': 'End the call',
  '1v1.hangUpBody': 'End the call with {name}?',
  '1v1.hangUpBodyWaiting': 'Leave this call?',
  '1v1.audienceDenied':
    'A 1v1 call is two publishing parties: the audience role is not allowed. Reload and join as host.',
  '1v1.full': 'This 1v1 call is already full (2 participants).',
  '1v1.inviteTitle': 'Invite to the call',
  '1v1.inviteNote':
    'Open this link on the other device; both sides join the same channel and publish.',
  '1v1.devicesTitle': 'Call devices',
  '1v1.deviceMic': 'Microphone',
  '1v1.deviceCamera': 'Camera',
  '1v1.deviceSpeaker': 'Speaker',
  '1v1.deviceDefaultMic': 'System default microphone',
  '1v1.deviceDefaultCamera': 'System default camera',
  '1v1.deviceDefaultSpeaker': 'System default speaker',
  '1v1.deviceNote':
    'Device switching is applied to the local preview; the published track follows the browser default.',
  '1v1.speaker': 'Speaker',
  '1v1.callControls': 'Call controls',
  '1v1.settings': 'Settings',
  '1v1.participants': 'Participants',
  '1v1.chat': 'Chat',

  /* ------------------------------------------------------------------ live */
  'live.title': 'Interactive live',
  'live.host': 'Host',
  'live.audience': 'Audience',
  'live.audienceCount': '{count} in the audience',
  'live.hostBadge': 'host · publishing',
  'live.audienceBadge': 'audience · subscribe only',
  'live.audienceNote':
    'You are in the audience. The RTC token grants join + subscribe only, so mic and camera stay disabled.',
  'live.stage': 'Host stage',
  'live.comments': 'Live comments',
  'live.commentsEmpty': 'No comments yet. Say hello!',
  'live.commentPlaceholder': 'Say something…',
  'live.commentLabel': 'Live comment',
  'live.sendComment': 'Send comment',
  'live.reactions': 'Reactions',
  'live.reaction.heart': 'Heart',
  'live.reaction.clap': 'Clap',
  'live.reaction.like': 'Like',
  'live.reaction.received': 'reaction',
  'live.manage': 'Host management',
  'live.manageTitle': 'Live host management',
  'live.mute': 'Mute',
  'live.kick': 'Remove from live',
  'live.promote': 'Invite on stage',
  'live.demote': 'Back to audience',
  'live.viewers': 'Audience',
  'live.onStage': 'On stage',
  'live.hostOnly': 'Only the host can open management.',
  'live.muteToast': 'Mute request sent to {name}',
  'live.kickToast': 'Remove request sent to {name}',
  'live.roleToast': '{name} is now {role}',
  'live.publishDenied': 'Audience cannot publish: the token has no publish privilege.',
  'live.publishDeniedToast': 'Publishing is denied for the audience role',
  'live.promoted': 'The host invited you on stage',
  'live.renew': 'Renew token',

  /* ------------------------------------------------------------------ voice */
  'voice.title': 'Voice chat room',
  'voice.seats': '{occupied}/{total} seats',
  'voice.seatLabel': 'Seat {index}',
  'voice.emptySeat': 'Empty seat',
  'voice.raiseHand': 'Raise hand',
  'voice.lowerHand': 'Lower hand',
  'voice.approve': 'Approve',
  'voice.approved': 'Approved',
  'voice.listener': 'Mic listener',
  'voice.host': 'Host',
  'voice.speaking': 'Speaking',
  'voice.micOn': 'Mic on',
  'voice.micOff': 'Mic off',
  'voice.listenerNote':
    'Mic listeners can raise a hand; the host approves them onto one of the 8 seats.',
  'voice.hostNote': 'Approve raised hands to put listeners on mic. Seats are audio-only.',
  'voice.hands': 'Raised hands',
  'voice.noHands': 'No raised hands yet.',
  'voice.chat': 'Room chat',
  'voice.chatPlaceholder': 'Message the room',
  'voice.chatEmpty': 'No messages yet.',
  'voice.joinAsListener': 'Join as mic listener',
  'voice.joinAsHost': 'Join as host',
  'voice.seatsFull': 'All 8 mic seats are taken',
  'voice.enter': 'Join the room',
  'voice.audioOnly': 'Audio only',
  'voice.raiseToast': 'Hand raised — waiting for the host',
  'voice.approveToast': '{name} is now on mic',
  'voice.hostRole': 'host',
  'voice.listenerRole': 'listener',
  'voice.mute': 'Mute',
  'voice.unmute': 'Unmute',
  'voice.renew': 'Renew token',

  /* ------------------------------------------------------------------ settings dialog shared */
  'settings.title': 'Settings',
  'settings.close': 'Close settings',
  'settings.scene': 'Scenario',
  'settings.session': 'Session',
  'settings.localUid': 'Local uid',
  'settings.rtt': 'RTT',
  'settings.bitrate': 'Bitrate',
} as const;

export type MessageKey = keyof typeof enUS;

/** zh-CN resource map. Key parity with `enUS` is enforced by the verify suite. */
export const zhCN: Record<MessageKey, string> = {
  'join.instant': '立即开一场会',
  'join.instantHint': '自动生成会议号并以主持人身份进入，把链接发给对方即可加入。',
  'join.guestName': '访客',
  'app.brand': '{brand} 会议',
  'app.tagline': '在线 · 实时音视频',
  'app.offlineTag': '离线演示',
  'lang.label': '语言',
  'lang.zh': '中文',
  'lang.en': 'English',
  'theme.toLight': '切换到浅色主题',
  'theme.toDark': '切换到深色主题',
  'theme.light': '浅色',
  'theme.dark': '深色',
  'common.close': '关闭',
  'common.cancel': '取消',
  'common.copy': '复制',
  'common.copied': '已复制',
  'common.send': '发送',
  'common.you': '你',
  'common.system': '系统',
  'common.unknown': '未知',
  'common.loading': '加载中…',
  'common.dash': '-',

  'scene.label': '场景',
  'scene.hint': '选择演示场景。URL 参数 ?scene= 会保留，便于分享。',
  'scene.meeting': '会议',
  'scene.meeting.title': '多人会议',
  'scene.meeting.desc': '画廊视图、主持人管理、聊天与白板——原有的 AgoraMeeting 对齐演示。',
  'scene.oneToOne': '1v1 通话',
  'scene.oneToOne.title': '一对一通话',
  'scene.oneToOne.desc': '双人通话，远端视频全屏，包含通话控制、邀请与 Token 续期。',
  'scene.live': '互动直播',
  'scene.live.title': '互动直播',
  'scene.live.desc': '主播推流、观众订阅与评论；Token 不授予观众推流权限。',
  'scene.voice': '语音房',
  'scene.voice.title': '语音聊天室',
  'scene.voice.desc': '纯音频 8 个麦位，房主审批举手，支持文字聊天。',

  'join.title': '加入会议',
  'join.subtitle': '基于 ni-meet SDK 的 UIKit 风格实时音视频体验',
  'join.lede':
    '输入主持人分享的会议号。加入后会把真实的摄像头与麦克风发布到在线 SFU，没有模拟媒体模式。',
  'join.ledeFake':
    '已开启离线演示模式：媒体在本标签页内模拟，不访问控制面。关闭后使用真实 Token 加入在线 SFU。',
  'join.field.channel': '会议号',
  'join.field.name': '你的名字',
  'join.field.role': '加入身份',
  'join.field.camera': '摄像头',
  'join.field.microphone': '麦克风',
  'join.field.scene': '场景',
  'join.placeholder.channel': 'weekly-sync',
  'join.placeholder.name': 'Ada Lovelace',
  'join.mic.on': '麦克风开',
  'join.mic.off': '麦克风关',
  'join.camera.on': '摄像头开',
  'join.camera.off': '摄像头关',
  'join.video.on': '视频开',
  'join.video.off': '视频关',
  'join.role.host': '主播 / 主持人',
  'join.role.audience': '观众',
  'join.role.listener': '连麦听众',
  'join.submit': '加入会议',
  'join.submitFake': '加入演示会议',
  'join.demo.toggle': '离线演示模式',
  'join.demo.hint':
    '在本标签页内模拟媒体面（不需要 LiveKit 边缘节点与 Token 端点），适合没有 Docker 环境的主机。',
  'join.device.defaultCamera': '默认摄像头',
  'join.device.defaultMicrophone': '默认麦克风',
  'join.device.cameraN': '摄像头 {index}',
  'join.device.microphoneN': '麦克风 {index}',
  'join.preview.off': '摄像头已关闭',
  'join.preview.unsupported':
    '摄像头预览需要支持媒体设备的浏览器（请用 localhost 或 HTTPS 打开本页）。',
  'join.preview.unavailable':
    '摄像头不可用（{reason}）。请在下方选择其他摄像头，或关闭摄像头加入。',
  'join.insecure':
    '当前来源禁止使用摄像头与麦克风。请改用 http://localhost:8083（或本机 127.0.0.1），其他设备请使用 HTTPS。',
  'join.note.online':
    '本页始终在线运行：Token 来自控制面，媒体经 LiveKit 传输。摄像头/麦克风需要安全上下文（localhost、127.0.0.1 或 HTTPS）。',
  'join.note.fake': '离线演示模式：Token 与媒体都在进程内模拟，不会访问 API 或 SFU。',
  'join.error.tokenEndpoint':
    '未配置 RTC Token 端点；本演示需要在线控制面。可使用 ?fake=1 进入离线演示。',
  'join.error.token': '无法从 {endpoint} 获取 RTC Token：{reason}',
  'join.error.channelRequired': '请输入会议号',
  'join.error.nameRequired': '请输入其他参会人看到的名字',
  'join.error.roleDenied': '{scene} 仅允许主播/主持人身份。请以主播身份加入以发布媒体。',
  'join.error.offlineDenied': '离线演示模式不需要控制面；取消勾选即可在线加入。',

  'room.sceneBadge': '{scene}',
  'room.reconnecting': '正在重连…本地媒体继续。',
  'room.locked': '会议已锁定：新参会人需要等待。',
  'room.recording.start': '开始云端录制',
  'room.recording.stop': '停止录制',
  'room.share.self': '你正在共享屏幕',
  'room.leave': '离开',
  'room.leaveTitle': '离开会议',
  'room.stay': '留下',
  'room.leaveBody': '离开 {channel}？',
  'room.leaveBodyHost': '离开 {channel}？主持人离开会在本浏览器内结束会议。',
  'room.controls': '会议控制',
  'room.renew': '续期 Token',
  'room.renewed': 'Token 已续期',
  'room.invite': '邀请',
  'room.settings': '设置',
  'room.participants': '参会人',
  'room.sceneLabel': '场景',

  'meeting.control.micOn': '静音麦克风',
  'meeting.control.micOff': '取消静音',
  'meeting.control.cameraOn': '关闭摄像头',
  'meeting.control.cameraOff': '打开摄像头',
  'meeting.control.share': '共享屏幕',
  'meeting.control.chat': '聊天',
  'meeting.control.board': '白板',
  'meeting.control.people': '参会人',
  'meeting.control.hand': '举手',
  'meeting.control.manage': '会议管理',
  'meeting.control.settings': '设置',
  'meeting.badge.micOff': '麦克风关',
  'meeting.badge.host': '主持人',
  'meeting.badge.quality.unknown': '未知',
  'meeting.badge.quality.excellent': '极佳',
  'meeting.badge.quality.good': '良好',
  'meeting.badge.quality.fair': '一般',
  'meeting.badge.quality.poor': '较差',
  'meeting.badge.quality.bad': '很差',
  'meeting.badge.quality.down': '断开',
  'meeting.participants.title': '参会人（{count}）',
  'meeting.participants.muteAll': '全体静音',
  'meeting.participants.lock': '锁定会议',
  'meeting.participants.locked': '已锁定',
  'meeting.participants.muted': '已静音',
  'meeting.participants.handUp': '举手',
  'meeting.participants.remove': '移除',
  'meeting.participants.makeHost': '设为主持人',
  'meeting.participants.makeAudience': '设为观众',
  'meeting.chat.title': '聊天',
  'meeting.chat.close': '关闭聊天',
  'meeting.chat.placeholder': '发消息给所有人',
  'meeting.chat.label': '聊天内容',
  'meeting.chat.historyRestored': '已从此浏览器恢复 {count} 条历史消息',
  'meeting.chat.clearHistory': '清除历史',
  'system.historyCleared': '已清除本地聊天记录',
  'meeting.board.title': '白板',
  'meeting.board.close': '关闭白板',
  'meeting.board.readOnly': '只有主持人可以绘制。你正在观看白板。',
  'meeting.board.label': '共享白板',
  'meeting.board.eraser': '橡皮',
  'meeting.board.width': '粗细',
  'meeting.board.undo': '撤销',
  'meeting.board.clear': '清空',
  'meeting.board.swatch': '颜色 {color}',
  'meeting.invite.title': '邀请他人',
  'meeting.invite.link': '会议链接',
  'meeting.invite.number': '会议号',
  'meeting.invite.members': '成员',
  'meeting.invite.membersCount': '本会议 {count} 人',
  'meeting.invite.copy': '复制链接',
  'meeting.invite.note': '在 IM 中分享此链接。IM Provider 会自动解析 RTC Token 与 uid。',
  'meeting.settings.title': '设置',
  'meeting.settings.devices': '麦克风 / 摄像头 / 扬声器',
  'meeting.settings.defaultMic': '系统默认麦克风',
  'meeting.settings.defaultCamera': '系统默认摄像头',
  'meeting.settings.allow': '授权并刷新',
  'meeting.settings.enumerating': '正在枚举设备…',
  'meeting.settings.deviceCounts': '{microphones} 麦克风 · {cameras} 摄像头 · {speakers} 扬声器',
  'meeting.settings.quality': '画质档位',
  'meeting.settings.qualityOption': '{resolution} / {fps}帧',
  'meeting.settings.modeLive': '在线 · 实时音视频',
  'meeting.settings.qualityNext': '画质将在下次发布时生效',
  'meeting.settings.vbg': '虚拟背景',
  'meeting.settings.vbg.upload': '上传图片',
  'meeting.settings.vbg.none': '无',
  'meeting.settings.vbg.blur': '模糊',
  'meeting.settings.vbg.image': '图片',
  'meeting.settings.vbg.uploadHint': '在下方上传图片即可使用「图片」背景',
  'meeting.settings.vbg.note': '合成只作用于本地预览；发布的轨道仍然是原始摄像头画面。',
  'meeting.settings.session': '会话',
  'meeting.settings.mode': '模式',
  'meeting.settings.localUid': '本地 uid',
  'meeting.settings.rtt': '往返时延',
  'meeting.settings.bitrate': '码率',
  'meeting.manage.title': '会议管理',
  'meeting.manage.muteAll': '全体静音',
  'meeting.manage.lock': '锁定会议',
  'meeting.manage.unlock': '解锁会议',
  'meeting.manage.remove': '移除',
  'meeting.manage.hostOnly': '管理仅限主持人：{allowed}',
  'meeting.manage.true': '是',
  'meeting.manage.false': '否',
  'toast.inviteCopied': '邀请链接已复制',
  'toast.mutedCount': '已静音 {count} 位参会人',
  'toast.participantRemoved': '参会人已移除',
  'toast.historyCleared': '已清除本地聊天记录',
  'toast.meetingLocked': '会议已锁定',
  'toast.meetingUnlocked': '会议已解锁',
  'toast.permissionDenied': '摄像头/麦克风权限被拒绝',
  'toast.reconnecting': '正在重连…',
  'toast.offlineSeeded': '离线演示：已模拟参会人加入',
  'toast.tokenExpiring': 'Token 即将过期，正在自动续期',
  'toast.tokenRenewed': 'Token 已续期',
  'toast.tokenExpired': '会议 Token 已过期',
  'toast.kicked': '主持人已将你移出会议',
  'toast.mutedByHost': '主持人已将你静音',
  'toast.approvedMic': '主持人已批准你的麦位',
  'toast.voiceSeatGranted': '{name} 已上麦',
  'toast.seatsFull': '8 个麦位已满',
  'toast.notHost': '只有主持人可以执行该操作',

  'system.joining': '正在加入 {channel}',
  'system.joined': '你已加入 {channel}',
  'system.left': '{name} 离开了会议',
  'system.userJoined': '{name} 加入了会议',
  'system.hostMuted': '主持人静音了 {count} 位参会人',
  'system.removed': '{name} 被主持人移除',
  'system.roleChanged': '角色变更：{name} 现在是{role}',
  'system.role.host': '主持人',
  'system.role.audience': '观众',
  'system.role.listener': '连麦听众',
  'system.guest': '用户 {uid}',

  '1v1.title': '一对一通话',
  '1v1.waiting': '正在等待对方加入…',
  '1v1.connected': '已与 {name} 接通',
  '1v1.remoteOff': '{name} 的摄像头已关闭',
  '1v1.you': '你',
  '1v1.invite': '邀请',
  '1v1.devices': '设备',
  '1v1.renew': '续期 Token',
  '1v1.renewed': 'Token 已续期',
  '1v1.mute': '静音',
  '1v1.unmute': '取消静音',
  '1v1.cameraOn': '打开摄像头',
  '1v1.cameraOff': '关闭摄像头',
  '1v1.hangUp': '挂断',
  '1v1.hangUpTitle': '结束通话',
  '1v1.hangUpBody': '结束与 {name} 的通话？',
  '1v1.hangUpBodyWaiting': '离开本次通话？',
  '1v1.audienceDenied': '1v1 通话是双方推流的场景，不允许观众身份。请刷新并以主播身份加入。',
  '1v1.full': '该 1v1 通话已满（2 人）。',
  '1v1.inviteTitle': '邀请加入通话',
  '1v1.inviteNote': '在另一台设备打开此链接；双方会加入同一频道并相互推流。',
  '1v1.devicesTitle': '通话设备',
  '1v1.deviceMic': '麦克风',
  '1v1.deviceCamera': '摄像头',
  '1v1.deviceSpeaker': '扬声器',
  '1v1.deviceDefaultMic': '系统默认麦克风',
  '1v1.deviceDefaultCamera': '系统默认摄像头',
  '1v1.deviceDefaultSpeaker': '系统默认扬声器',
  '1v1.deviceNote': '设备切换作用于本地预览；发布的轨道跟随浏览器默认设备。',
  '1v1.speaker': '扬声器',
  '1v1.callControls': '通话控制',
  '1v1.settings': '设置',
  '1v1.participants': '参会人',
  '1v1.chat': '聊天',

  'live.title': '互动直播',
  'live.host': '主播',
  'live.audience': '观众',
  'live.audienceCount': '观众 {count} 人',
  'live.hostBadge': '主播 · 推流中',
  'live.audienceBadge': '观众 · 仅订阅',
  'live.audienceNote': '你当前是观众。RTC Token 只授予加入与订阅权限，因此麦克风和摄像头保持关闭。',
  'live.stage': '主播画面',
  'live.comments': '直播评论',
  'live.commentsEmpty': '还没有评论，来打个招呼吧！',
  'live.commentPlaceholder': '说点什么…',
  'live.commentLabel': '直播评论',
  'live.sendComment': '发送评论',
  'live.reactions': '互动表情',
  'live.reaction.heart': '爱心',
  'live.reaction.clap': '鼓掌',
  'live.reaction.like': '点赞',
  'live.reaction.received': '表情',
  'live.manage': '主播管理',
  'live.manageTitle': '直播主播管理',
  'live.mute': '静音',
  'live.kick': '移出直播',
  'live.promote': '邀请上麦',
  'live.demote': '回到观众席',
  'live.viewers': '观众',
  'live.onStage': '台上',
  'live.hostOnly': '只有主播可以打开管理面板。',
  'live.muteToast': '已向 {name} 发送静音请求',
  'live.kickToast': '已向 {name} 发送移出请求',
  'live.roleToast': '{name} 现在是{role}',
  'live.publishDenied': '观众不能推流：Token 未授予推流权限。',
  'live.publishDeniedToast': '观众身份禁止推流',
  'live.promoted': '主播已将你邀请上台',
  'live.renew': '续期 Token',

  'voice.title': '语音聊天室',
  'voice.seats': '{occupied}/{total} 麦位',
  'voice.seatLabel': '麦位 {index}',
  'voice.emptySeat': '空麦位',
  'voice.raiseHand': '举手',
  'voice.lowerHand': '放下手',
  'voice.approve': '批准上麦',
  'voice.approved': '已批准',
  'voice.listener': '连麦听众',
  'voice.host': '房主',
  'voice.speaking': '正在说话',
  'voice.micOn': '麦克风开',
  'voice.micOff': '麦克风关',
  'voice.listenerNote': '连麦听众可以举手，房主批准后即可上麦（共 8 个麦位）。',
  'voice.hostNote': '批准举手即可让听众上麦。麦位仅传输音频。',
  'voice.hands': '举手列表',
  'voice.noHands': '暂无举手。',
  'voice.chat': '房间聊天',
  'voice.chatPlaceholder': '发消息到房间',
  'voice.chatEmpty': '还没有消息。',
  'voice.joinAsListener': '以连麦听众身份加入',
  'voice.joinAsHost': '以房主身份加入',
  'voice.seatsFull': '8 个麦位已满',
  'voice.enter': '进入房间',
  'voice.audioOnly': '纯音频',
  'voice.raiseToast': '已举手，等待房主批准',
  'voice.approveToast': '{name} 已上麦',
  'voice.hostRole': '房主',
  'voice.listenerRole': '听众',
  'voice.mute': '静音',
  'voice.unmute': '取消静音',
  'voice.renew': '续期 Token',

  'settings.title': '设置',
  'settings.close': '关闭设置',
  'settings.scene': '场景',
  'settings.session': '会话',
  'settings.localUid': '本地 uid',
  'settings.rtt': '往返时延',
  'settings.bitrate': '码率',
};

export const RESOURCES = { 'en-US': enUS, 'zh-CN': zhCN } as const;
export type LocaleCode = keyof typeof RESOURCES;
export const LOCALES: LocaleCode[] = ['en-US', 'zh-CN'];

export function isLocaleCode(value: unknown): value is LocaleCode {
  return value === 'en-US' || value === 'zh-CN';
}

/** Placeholder names in a template, used by the key-parity test. */
export function placeholders(template: string): string[] {
  return [...template.matchAll(/\{([a-zA-Z0-9_]+)\}/g)].map((match) => match[1] ?? '').sort();
}

/** Locale-independent translator (also usable from non-React modules such as the session). */
export function translate(
  locale: LocaleCode,
  key: MessageKey,
  params?: Record<string, string | number | boolean | null | undefined>,
): string {
  const template = RESOURCES[locale]?.[key] ?? RESOURCES['en-US'][key];
  if (typeof template !== 'string') return `⟦${String(key)}⟧`;
  if (!params) return template;
  return template.replace(/\{([a-zA-Z0-9_]+)\}/g, (match, name: string) => {
    const value = params[name];
    return value === undefined || value === null ? match : String(value);
  });
}
